import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    MapPinIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    FunnelIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Navbar from "./Navbar";
import Footer from "../SharedComponents/Footer";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
}

type SortOption = "" | "most_places" | "least_places" | "alphabetical";

/* ── Skeleton Card ── */
const CityCardSkeleton: React.FC = () => (
    <div className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm animate-pulse">
        <div className="w-full h-44 bg-gray-200" />
        <div className="p-4 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-3 bg-gray-200 rounded w-1/3" />
        </div>
    </div>
);

/* ── City Card ── */
const CityCard: React.FC<{ city: City }> = ({ city }) => {
    const [loaded, setLoaded] = useState(false);

    const imageUrl = city.image
        ? city.image.startsWith("http")
            ? city.image
            : `${BASE_URL}${city.image}`
        : null;

    return (
        <Link
            to={`/places/city/${encodeURIComponent(city.name)}`}
            className="group rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
        >
            <div className="relative w-full h-44 bg-gray-100 overflow-hidden flex-shrink-0">
                {imageUrl ? (
                    <>
                        <img
                            src={imageUrl}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl brightness-90"
                        />
                        <img
                            src={imageUrl}
                            alt={city.name}
                            onLoad={() => setLoaded(true)}
                            className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${
                                loaded ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    </>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                )}
            </div>

            <div className="p-4 flex items-center justify-between">
                <h2 className="text-sm font-myYekanDemibold text-gray-800 group-hover:text-green-700 transition-colors">
                    {city.name}
                </h2>
                {!!city.place_count && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 font-myYekanFaNumRegular">
                        <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{city.place_count} مکان</span>
                    </div>
                )}
            </div>
        </Link>
    );
};

/* ── Sort Bottom Sheet (mobile) ── */
const SortBottomSheet: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    sortBy: SortOption;
    onSelect: (v: SortOption) => void;
}> = ({ isOpen, onClose, sortBy, onSelect }) => {
    const options: { value: SortOption; label: string }[] = [
        { value: "", label: "پیش‌فرض" },
        { value: "most_places", label: "بیشترین مکان" },
        { value: "least_places", label: "کمترین مکان" },
        { value: "alphabetical", label: "حروف الفبا" },
    ];

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
            <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl font-myYekanRegular">
                <div className="flex justify-center pt-3 pb-2">
                    <div className="w-12 h-1 bg-gray-300 rounded-full" />
                </div>
                <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                    <h2 className="text-base font-myYekanDemibold text-gray-800">مرتب‌سازی</h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition">
                        <XMarkIcon className="h-5 w-5 text-gray-600" />
                    </button>
                </div>
                <div className="p-4 space-y-2 pb-8">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => { onSelect(opt.value); onClose(); }}
                            className={`w-full text-right px-4 py-3 rounded-xl text-sm transition flex items-center justify-between ${
                                sortBy === opt.value
                                    ? "bg-green-50 border border-green-200 text-green-700 font-myYekanDemibold"
                                    : "hover:bg-gray-50 text-gray-700"
                            }`}
                        >
                            <span>{opt.label}</span>
                            {sortBy === opt.value && (
                                <div className="w-2 h-2 rounded-full bg-green-600" />
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </>
    );
};

/* ── Main Page ── */
const CitiesPage: React.FC = () => {
    const [searchValue, setSearchValue] = useState("");
    const [sortBy, setSortBy] = useState<SortOption>("");
    const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);

    const { data: cities = [], isLoading, isError } = useQuery<City[]>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        staleTime: 10 * 60 * 1000,
    });

    /* Client-side filter + sort */
    const filtered = useMemo(() => {
        let result = [...cities];

        if (searchValue.trim()) {
            result = result.filter((c) =>
                c.name.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        if (sortBy === "most_places") result.sort((a, b) => b.place_count - a.place_count);
        else if (sortBy === "least_places") result.sort((a, b) => a.place_count - b.place_count);
        else if (sortBy === "alphabetical") result.sort((a, b) => a.name.localeCompare(b.name, "fa"));

        return result;
    }, [cities, searchValue, sortBy]);

    const hasActiveFilter = !!searchValue.trim() || !!sortBy;

    const handleReset = () => {
        setSearchValue("");
        setSortBy("");
    };

    const sortLabels: Record<SortOption, string> = {
        "": "مرتب‌سازی",
        most_places: "بیشترین مکان",
        least_places: "کمترین مکان",
        alphabetical: "حروف الفبا",
    };

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            <div className="max-w-8xl mx-auto pt-10 pb-10 px-[24px] tablet:px-20 desktop:px-36">

                {/* Header */}
                <div className="mb-6">

                    <div className="flex items-baseline gap-3">
                        <h1 className="text-2xl md:text-3xl font-myYekanDemibold text-gray-900">
                            شهرها
                        </h1>
                        {!isLoading && (
                            <span className="text-sm text-gray-400 font-myYekanFaNumRegular">
                                {filtered.length} شهر
                            </span>
                        )}
                    </div>
                </div>

                {/* ── Search + Sort bar ── */}
                <div className="flex items-center gap-2 mb-6">
                    {/* Search */}
                    <div className="flex-1 flex items-center bg-white border border-gray-200 rounded-2xl px-3 py-2 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                        <button
                            className="ml-2 flex-shrink-0"
                            onClick={() => {}} 
                        >
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        </button>
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="جستجوی شهر..."
                            className="flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none font-myYekanRegular py-1"
                        />
                        {searchValue && (
                            <button
                                onClick={() => setSearchValue("")}
                                className="mr-1 flex-shrink-0 p-0.5 hover:bg-gray-100 rounded-full transition"
                            >
                                <XMarkIcon className="h-4 w-4 text-gray-400" />
                            </button>
                        )}
                    </div>

                    {/* Sort button — desktop: inline dropdown, mobile: bottom sheet */}
                    {/* Desktop sort select */}
                    <div className="hidden lg:flex items-center bg-white border border-gray-200 rounded-2xl px-3 py-3 shadow-[0_2px_10px_rgba(0,0,0,0.04)] gap-2 min-w-[160px]">
                        <FunnelIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            className="flex-1 bg-transparent text-sm text-gray-700 focus:outline-none font-myYekanRegular appearance-none cursor-pointer"
                        >
                            <option value="">مرتب‌سازی</option>
                            <option value="most_places">بیشترین مکان</option>
                            <option value="least_places">کمترین مکان</option>
                            <option value="alphabetical">حروف الفبا</option>
                        </select>
                        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>

                    {/* Mobile sort button */}
                    <button
                        onClick={() => setIsSortSheetOpen(true)}
                        className={`lg:hidden relative flex items-center justify-center w-12 h-12 rounded-2xl border shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:bg-gray-50 transition flex-shrink-0 ${
                            sortBy
                                ? "bg-green-50 border-green-200"
                                : "bg-white border-gray-200"
                        }`}
                    >
                        <FunnelIcon className={`h-5 w-5 ${sortBy ? "text-green-600" : "text-gray-700"}`} />
                        {sortBy && (
                            <span className="absolute -top-1 -right-1 bg-green-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                                1
                            </span>
                        )}
                    </button>
                </div>

                {/* Active filter chips */}
                {hasActiveFilter && (
                    <div className="flex flex-wrap items-center gap-2 mb-5">
                        {searchValue.trim() && (
                            <span className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-myYekanRegular px-3 py-1.5 rounded-full">
                                جستجو: {searchValue}
                                <button onClick={() => setSearchValue("")}>
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        )}
                        {sortBy && (
                            <span className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-myYekanRegular px-3 py-1.5 rounded-full">
                                {sortLabels[sortBy]}
                                <button onClick={() => setSortBy("")}>
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        )}
                        <button
                            onClick={handleReset}
                            className="text-xs text-gray-400 hover:text-red-500 font-myYekanRegular transition"
                        >
                            پاک کردن همه
                        </button>
                    </div>
                )}

                {/* Grid */}
                {isError ? (
                    <div className="text-center py-20 text-red-500 font-myYekanFaNumRegular">
                        خطا در بارگذاری شهرها. لطفاً دوباره تلاش کنید.
                    </div>
                ) : isLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <CityCardSkeleton key={i} />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-gray-500 bg-white rounded-lg border font-myYekanFaNumRegular">
                        <p className="text-lg mb-2">شهری یافت نشد</p>
                        <p className="text-sm">لطفاً عبارت جستجوی خود را تغییر دهید.</p>
                        <button
                            onClick={handleReset}
                            className="mt-4 text-green-600 hover:underline text-sm font-myYekanRegular"
                        >
                            پاک کردن فیلترها
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {filtered.map((city) => (
                            <CityCard key={city.id} city={city} />
                        ))}
                    </div>
                )}
            </div>

            {/* Mobile sort bottom sheet */}
            <SortBottomSheet
                isOpen={isSortSheetOpen}
                onClose={() => setIsSortSheetOpen(false)}
                sortBy={sortBy}
                onSelect={setSortBy}
            />

            <Footer />
        </div>
    );
};

export default CitiesPage;
